// src/pages/use-cases/EdtechPage.js
// Targets: educational video translation, online course dubbing, MOOC localization
import UseCasePage from './UseCasePage';

const DATA = {
  slug: 'edtech',
  breadcrumbLabel: 'EdTech',
  seo: {
    title: 'AI Video Dubbing for EdTech & Online Courses | DubLabs',
    description: 'Localize online courses into 32 languages with DubLabs AI dubbing. Preserve the instructor\'s voice across markets. No re-recording, no studio bookings.',
  },
  eyebrow: 'For EdTech',
  headline: 'One instructor. Every language your students speak.',
  subhead: 'DubLabs localizes online courses, lectures, and educational video into 32 languages while preserving the original instructor\'s voice — so learners in Hindi, Spanish, or Portuguese hear the same teacher, not a different one.',
  problem: 'Course catalogs sit trapped in one language. Traditional localization means re-recording every lesson with a different voice actor per language — students in different markets get different "teachers" and inconsistent learning experiences. Costs balloon, and updating a single module means re-booking a studio.',
  features: [
    {
      number: '01',
      title: 'One instructor across every market',
      body: 'The teacher your students trust in English is the same voice they hear in Hindi or Spanish. Consistent tone, personality, and pedagogy across every localized version — impossible with actor-recasted studio dubbing.',
    },
    {
      number: '02',
      title: 'Update a lesson in minutes, not weeks',
      body: 'Curriculum change? Re-dub the affected module in every language in minutes — same voice, same tone. No booking a studio, no re-recording sessions, no waiting for the actor\'s next available slot.',
    },
    {
      number: '03',
      title: 'Localize back-catalogs economically',
      body: 'Traditional dubbing quotes for a 50-hour course library run into six figures per language. DubLabs handles the same catalog at a fraction of the cost — economical to launch in 3–4 new language markets simultaneously.',
    },
    {
      number: '04',
      title: 'Long-form quality without drift',
      body: 'Purpose-built for the multi-hour lecture format. Same voice model holds across a full course without the drift and re-clone artifacts that AI-avatar tools produce on long-form content.',
    },
    {
      number: '05',
      title: 'Frame-accurate lip-sync for talking-head lectures',
      body: 'Most educational video is instructor-on-camera. Our lip-sync AI re-syncs the instructor\'s mouth to the dubbed audio frame-by-frame so localized lessons look native, not out-of-sync. No reshoots, works on your existing lecture library.',
    },
  ],
  scenarios: [
    {
      label: 'Scenario 01',
      headline: 'Online course platform localizes their top 10 courses for the Indian market.',
      body: '10 courses × 20 hours each = 200 hours of English content. Studios quoted 6 months and ~$400K to dub into Hindi. DubLabs localizes the entire library in a week at a fraction of the cost — same instructor voices, ready for enrollment in the new market.',
    },
    {
      label: 'Scenario 02',
      headline: 'Corporate training team ships compliance modules in every regional office\'s language.',
      body: 'Quarterly compliance training goes out in 5 languages: English, Spanish, Mandarin, Hindi, Portuguese. DubLabs dubs the module in all 5 languages the same day it\'s finalized — same executive voice explaining the same policy, no localization delay between global HQ and regional offices.',
    },
    {
      label: 'Scenario 03',
      headline: 'MOOC provider adds Hindi + Tamil to a flagship course to unlock Indian enrollment.',
      body: '80-hour flagship course, previously English-only. DubLabs adds Hindi and Tamil in parallel, same professor voice throughout. The course opens to hundreds of millions of learners in a market that previously had no access to the original instruction quality.',
    },
  ],
  faqs: [
    {
      q: 'Will the dubbed course sound like the same instructor across every language?',
      a: 'Yes — that\'s the whole point. DubLabs clones the instructor\'s voice once from their original video. Every localized version uses that cloned voice, so students in every market hear the same teacher speaking their language. Studios can\'t do this — they recast each language with a different voice actor.',
    },
    {
      q: 'What about technical vocabulary, jargon, and formulas?',
      a: 'Our translation preserves technical terminology and jargon rather than translating it into vague equivalents. For math and code lectures, formulas + variable names stay intact on screen while the surrounding explanation is dubbed. For deep-technical content, we recommend a human review pass on the translated script before dubbing — we support that workflow.',
    },
    {
      q: 'How does DubLabs handle multi-hour lectures vs short clips?',
      a: 'DubLabs is optimized for the long-form format — voice model consistency holds across a full 90-minute lecture without drift. AI-avatar tools like HeyGen typically drift on long-form. See /vs/heygen for more.',
    },
    {
      q: 'Can students switch between language versions of the same course?',
      a: 'Yes. Deliver dubbed outputs as separate courses per language, or as multi-language audio tracks on a single video (supported on YouTube, Vimeo, most LMS platforms). Analytics roll up as you prefer.',
    },
    {
      q: 'How do we handle content updates and versioning?',
      a: 'When you update a lesson, we re-dub only the changed segments in every language — you don\'t re-do the entire module. Turnaround is minutes per language. Compare that to a studio schedule where every update triggers a fresh booking cycle.',
    },
    {
      q: 'Does lip-sync work on instructor-on-camera lectures?',
      a: 'Yes — this is the format lip-sync helps most with. Our AI re-syncs the instructor\'s mouth movements to the dubbed audio frame-by-frame. Students in Hindi or Spanish see the instructor actually speaking their language, not a mouth-audio mismatch that breaks the learning experience.',
    },
  ],
};

export default function EdtechPage() {
  return <UseCasePage {...DATA} />;
}
